import React, { useState, useRef, useEffect } from "react";
import {
  Stage,
  Layer,
  Rect,
  Text,
  Image as KonvaImage,
  Transformer,
  Group,
} from "react-konva";
import useImage from "use-image";
import JsBarcode from "jsbarcode";
import ProgressBar from './ProgressBar';
import StampsContainer from './StampsContainer';
import { debounce } from 'lodash';

const DraggableResizable = ({ shapeProps, isSelected, onSelect, onChange, children }) => {
  const shapeRef = useRef();
  const trRef = useRef();

  useEffect(() => {
    if (isSelected) {
      trRef.current?.nodes([shapeRef.current]);
      trRef.current?.getLayer().batchDraw();
    }
  }, [isSelected]);

  const handleTransformEnd = () => {
    if (!shapeProps.resizable) return;

    const node = shapeRef.current;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    // Reset scaling
    node.scaleX(1);
    node.scaleY(1);

    // Get the new dimensions
    const newWidth = Math.round(node.width() * scaleX);
    const newHeight = Math.round(node.height() * scaleY);

    // Get position
    const { x, y } = node.position();

    // Update based on element type
    if ("fontSize" in shapeProps) {
      // Text elements handling
      const newFontSize = Math.round(shapeProps.fontSize * scaleX);
      onChange({
        ...shapeProps,
        x: node.x(),
        y: node.y(),
        width: Math.round(node.width() * scaleX),
        height: Math.round(node.height() * node.scaleY()),
        fontSize: Math.min(Math.max(newFontSize, 10), 100), // Clamp between 10 and 100
      });
    } else {
      // Handle all other elements including progress bar
      onChange({
        ...shapeProps,
        x,
        y,
        width: newWidth,
        height: newHeight,
      });
    }

    // Deselect after transform
    setTimeout(() => onSelect(null), 0);
  };

  return (
    <>
      <Group
        ref={shapeRef}
        draggable={shapeProps.draggable}
        {...shapeProps}
        onClick={onSelect}
        onTap={onSelect}


        // code to bound the draggable area
        dragBoundFunc={(pos) => {
          const node = shapeRef.current;
        
          // Approximate width/height safely
          const box = node.getClientRect({ skipTransform: true });
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();
        
          const width = box.width * scaleX;
          const height = box.height * scaleY;
        
          const minX = 0;
          const minY = 0;
          const maxX = 700 - width;
          const maxY = 350 - height;
        
          return {
            x: Math.min(Math.max(pos.x, minX), maxX),
            y: Math.min(Math.max(pos.y, minY), maxY),
          };
        }}
        // code to bound the draggable area



        onDragEnd={(e) => {
          if (shapeProps.draggable) {
            onChange({
              ...shapeProps,
              x: e.target.x(),
              y: e.target.y(),
            });
          }
        }}
        onTransformEnd={handleTransformEnd}
      >
        {children}
      </Group>
      {isSelected && shapeProps.resizable && (
        <Transformer
          ref={trRef}
          resizeEnabled={shapeProps.resizable}
          rotateEnabled={false}
          keepRatio={"fontSize" in shapeProps} // Keep ratio only for text elements
        />
      )}
    </>
  );
};

const BarcodeImage = ({ color, bgColor, width, height }) => {
  const [barcodeImage, setBarcodeImage] = useState(null);

  // Debounced barcode generation
  const generateBarcode = useRef(
    debounce((color, bgColor, width, height) => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const barHeight = height * 0.8;
      const barWidth = Math.max(1, Math.floor(width / 50));

      JsBarcode(canvas, "1234567890", {
        format: "CODE128",
        width: barWidth,
        height: barHeight,
        displayValue: false,
        lineColor: color,
        background: bgColor,
        margin: 0,
        padding: 0,
        textMargin: 0
      });

      const image = new window.Image();
      image.src = canvas.toDataURL("image/png");
      image.onload = () => setBarcodeImage(image);
    }, 300) // 300ms delay
  ).current;

  useEffect(() => {
    generateBarcode(color, bgColor, width, height);
    
    return () => {
      generateBarcode.cancel(); // Cleanup
    };
  }, [color, bgColor, width, height]);

  // Show previous barcode while generating new one
  return barcodeImage ? (
    <KonvaImage 
      image={barcodeImage} 
      width={width} 
      height={height}
      offsetX={0}
      offsetY={0}
      listening={true} // Enable interactions
    />
  ) : null;
};

const LoyaltyCardEditor = ({
  logo,
  background,
  textElements,
  logoSettings, // Add this prop
  onElementSelect,
  selectedElement,
  barcodeSettings,
  onElementChange,
  progressBarStyles,
  rewardStyle,
  stampContainerStyles,
  stampStyles,
  stamps,
  threshold,
  stampinLogoSettings,
}) => {
  const [logoImage] = useImage(logo || ""); // Update reference
  const [backgroundImage] = useImage(
    background.type === "image" ? background.value : ""
  ); 
  const [stampinLogo] = useImage(stampinLogoSettings.defaultImage);

  const cardWidth = 700;
  const cardHeight = 350;

  const handleElementChange = (elementKey, newProps) => {
    onElementChange(elementKey, newProps);
  };

  // Add new function to calculate image scaling
  const calculateImageScale = (image, containerWidth, containerHeight) => {
    if (!image) return { scale: 1, x: 0, y: 0 };

    const imageRatio = image.width / image.height;
    const containerRatio = containerWidth / containerHeight;
    let scale;
    let x = 0;
    let y = 0;

    if (containerRatio > imageRatio) {
      // Container is wider than image
      scale = containerWidth / image.width;
      y = (containerHeight - image.height * scale) / 2;
    } else {
      // Container is taller than image
      scale = containerHeight / image.height;
      x = (containerWidth - image.width * scale) / 2;
    }

    return { scale, x, y };
  };

  // Get background image dimensions and calculate scaling
  const imageScaling = React.useMemo(() => {
    if (background.type === "image" && backgroundImage) {
      return calculateImageScale(backgroundImage, cardWidth, cardHeight);
    }
    return null;
  }, [backgroundImage, cardWidth, cardHeight]);

  return (
    <Stage width={cardWidth} height={cardHeight}>
      <Layer>
        {/* Base white background with corner radius */}
        <Rect
          x={0}
          y={0}
          width={cardWidth}
          height={cardHeight}
          fill="#ffffff"
          cornerRadius={10}
        />

        {/* Actual background (color or image) with clipping */}
        <Group
          clipFunc={(ctx) => {
            ctx.beginPath();
            ctx.moveTo(0, 10);
            ctx.lineTo(0, cardHeight - 10);
            ctx.quadraticCurveTo(0, cardHeight, 10, cardHeight);
            ctx.lineTo(cardWidth - 10, cardHeight);
            ctx.quadraticCurveTo(
              cardWidth,
              cardHeight,
              cardWidth,
              cardHeight - 10
            );
            ctx.lineTo(cardWidth, 10);
            ctx.quadraticCurveTo(cardWidth, 0, cardWidth - 10, 0);
            ctx.lineTo(10, 0);
            ctx.quadraticCurveTo(0, 0, 0, 10);
            ctx.closePath();
          }}
        >
          {/* Color background */}
          <Rect
            x={0}
            y={0}
            width={cardWidth}
            height={cardHeight}
            fill={background.type === "color" ? background.value : undefined}
          />

          {/* Image background */}
          {background.type === "image" && backgroundImage && (
            <KonvaImage
              x={imageScaling?.x || 0}
              y={imageScaling?.y || 0}
              image={backgroundImage}
              width={backgroundImage.width * (imageScaling?.scale || 1)}
              height={backgroundImage.height * (imageScaling?.scale || 1)}
              listening={false}
            />
          )}
        </Group>

        {/* Card shadow */}
        <Rect
          x={0}
          y={0}
          width={cardWidth}
          height={cardHeight}
          cornerRadius={10}
          shadowBlur={5}
          shadowColor="rgba(0,0,0,0.1)"
          fillEnabled={false}
          stroke="rgba(0,0,0,0.05)"
          strokeWidth={1}
        />

        {/* Business Name */}
        <DraggableResizable
          shapeProps={textElements.businessName}
          isSelected={selectedElement === "businessName"}
          onSelect={() => onElementSelect("businessName")}
          onChange={(newProps) => handleElementChange("businessName", newProps)}
        >
          <Text
            text={textElements.businessName.text}
            fontFamily={textElements.businessName.fontFamily}
            fontSize={textElements.businessName.fontSize}
            fill={textElements.businessName.color}
            fontStyle={textElements.businessName.fontStyle}
          />
        </DraggableResizable>

        {/* Tagline */}
        <DraggableResizable
          shapeProps={textElements.tagline}
          isSelected={selectedElement === "tagline"}
          onSelect={() => onElementSelect("tagline")}
          onChange={(newProps) => handleElementChange("tagline", newProps)}
        >
          <Text
            text={textElements.tagline.text}
            fontFamily={textElements.tagline.fontFamily}
            fontSize={textElements.tagline.fontSize}
            fill={textElements.tagline.color}
            fontStyle={textElements.tagline.fontStyle}
          />
        </DraggableResizable>

        {/* Contact Details */}
        <DraggableResizable
          shapeProps={textElements.contactDetails}
          isSelected={selectedElement === "contactDetails"}
          onSelect={() => onElementSelect("contactDetails")}
          onChange={(newProps) =>
            handleElementChange("contactDetails", newProps)
          }
        >
          <Text
            text={textElements.contactDetails.text}
            fontFamily={textElements.contactDetails.fontFamily}
            fontSize={textElements.contactDetails.fontSize}
            fill={textElements.contactDetails.color}
            fontStyle={textElements.contactDetails.fontStyle}
          />
        </DraggableResizable>

        {/* Customer ID */}
        <DraggableResizable
          shapeProps={textElements.customerId}
          isSelected={selectedElement === "customerId"}
          onSelect={() => onElementSelect("customerId")}
          onChange={(newProps) => handleElementChange("customerId", newProps)}
        >
          <Text
            text={textElements.customerId.text}
            fontFamily={textElements.customerId.fontFamily}
            fontSize={textElements.customerId.fontSize}
            fill={textElements.customerId.color}
            fontStyle={textElements.customerId.fontStyle}
          />
        </DraggableResizable>

        {/* Progress Text */}
        {rewardStyle && 
        <DraggableResizable
          shapeProps={textElements.progressText}
          isSelected={selectedElement === "progressText"}
          onSelect={() => onElementSelect("progressText")}
          onChange={(newProps) => handleElementChange("progressText", newProps)}
        >
          <Text
            text={textElements.progressText.text}
            fontFamily={textElements.progressText.fontFamily}
            fontSize={textElements.progressText.fontSize}
            fill={textElements.progressText.color}
            fontStyle={textElements.progressText.fontStyle}
          />
        </DraggableResizable>
          }

        {/* Offer Text (previously Reward Text) */}
        <DraggableResizable
          shapeProps={textElements.offerText}
          isSelected={selectedElement === "offerText"}
          onSelect={() => onElementSelect("offerText")}
          onChange={(newProps) => handleElementChange("offerText", newProps)}
        >
          <Text
            text={textElements.offerText.text}
            fontFamily={textElements.offerText.fontFamily}
            fontSize={textElements.offerText.fontSize}
            fill={textElements.offerText.color}
            fontStyle={textElements.offerText.fontStyle}
          />
        </DraggableResizable>

        {/* Logo */}
        <DraggableResizable
          shapeProps={logoSettings} // Use logoSettings instead of textElements.logo
          isSelected={selectedElement === "logo"}
          onSelect={() => onElementSelect("logo")}
          onChange={(newProps) => handleElementChange("logo", newProps)}
        >
          <KonvaImage
            image={logoImage}
            width={logoSettings.width}
            height={logoSettings.height}
            cornerRadius={
              logoSettings.borderRadius * (logoSettings.width / 100)
            }
          />
        </DraggableResizable>

        {/* Barcode */}
        <DraggableResizable
          shapeProps={barcodeSettings}
          isSelected={selectedElement === "barcode"}
          onSelect={() => onElementSelect("barcode")}
          onChange={(newProps) => handleElementChange("barcode", newProps)}
        >
          <BarcodeImage
            color={barcodeSettings.color}
            bgColor={barcodeSettings.bgColor}
            width={barcodeSettings.width}
            height={barcodeSettings.height}
          />
        </DraggableResizable>

        {/* Progress Bar or Stamps based on rewardStyle */}
        {rewardStyle === 'progress' ? (
          <DraggableResizable
            shapeProps={progressBarStyles}
            isSelected={selectedElement === "progressBar"}
            onSelect={() => onElementSelect("progressBar")}
            onChange={(newProps) => handleElementChange("progressBar", newProps)}
          >
            <ProgressBar styles={progressBarStyles} />
          </DraggableResizable>
        ) : rewardStyle === 'stamps' && stamps?.length > 0 && threshold > 0? (
          <DraggableResizable
            shapeProps={stampContainerStyles}
            isSelected={selectedElement === "stampContainer"}
            onSelect={() => onElementSelect("stampContainer")}
            onChange={(newProps) => handleElementChange("stampContainer", newProps)}
          >
            <StampsContainer
              containerStyles={stampContainerStyles}
              stampStyles={stampStyles}
              stamps={stamps}
            />
          </DraggableResizable>
        ) : null}

        {/* Stampin Logo */}
        {stampinLogoSettings?.visible && (
          <DraggableResizable
            shapeProps={stampinLogoSettings}
            isSelected={selectedElement === "stampinLogo"}
            onSelect={() => onElementSelect("stampinLogo")}
            onChange={(newProps) => handleElementChange("stampinLogo", newProps)}
          >
            <KonvaImage
              image={stampinLogo}
              width={stampinLogoSettings.width}
              height={stampinLogoSettings.height}
            />
          </DraggableResizable>
        )}
      </Layer>
    </Stage>
  );
};

export default LoyaltyCardEditor;


